import { can, PortalPermissions } from "../../../config/permissions";
import React, {FC, useEffect, useState} from 'react';
import {ContractsServices} from "../services/ContractsServices";
import {
  Contract,
  ContractRegister,
} from "../types";
import LoadingOverlay from "../../../components/LoadingOverlay";
import {formatBusinessName, formatDocument} from "../../../utils/generalFormat";
import {toast} from "react-toastify";
import {addSignatureInfoToPdf} from "../../../utils/pdfUtils";
import useGetMe from '../../../hooks/useCurrentUser';

import {
  ContainerLight,
  ContractInfo,
  DownloadButton,
  EmptyState,
  HeaderLight,
  HistoryCard,
  HistoryCardTitle,
  HistoryContainer,
  FilterContainer,
  FilterInput,
  FilterButton,
  PaginationContainer,
  PaginationButton,
  PageInfo
} from "../styles";

const ContractsHistory:FC = () => {
  const user = useGetMe();
  const contractsServices = new ContractsServices();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [signedContracts, setSignedContracts] = useState<Contract[]>([]);
  const [signedContractsRegister, setSignedContractsRegister] = useState<ContractRegister[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [merchantId, setMerchantId] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const fetchSignedContracts = async () => {
    try {
      setIsLoading(true);
      let response;

      if (user.userType === 3) {
        // Admin: pode filtrar por merchantId
        response = await contractsServices.getAllSignedContracts(
          merchantId || null,
          pageNumber,
          pageSize
        );
      } else {
        // Usuário comum
        response = await contractsServices.getSignedContracts(pageNumber, pageSize);
      }

      // Se retornou menos registros que o pageSize, não há mais páginas
      setHasMore(response && response.length === pageSize);

      return response;
    }
    catch (error) {
      console.log(error);
      toast.error('Erro ao carregar contratos');
      return [];
    }
    finally {
      setIsLoading(false);
    }
  };

  const fetchContracts = async (unsignedContractsIds: string[]): Promise<Contract[]> => {
    try {
      setIsLoading(true);
      return await contractsServices.getContracts(unsignedContractsIds);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadContract = async (contract: Contract) => {
    try {
      setDownloadingId(contract.id);
      toast.info('Preparando PDF para download...');

      // Formata a data
      const formattedDate = new Date(contract.signatureDate as string).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      // Processa o PDF
      const pdfBlob = await addSignatureInfoToPdf(
        contract.base64Content,
        {
          signatureDate: formattedDate,
          businessName: formatBusinessName(contract.businessName),
          document: formatDocument(contract.document),
        }
      );

      // Cria URL e faz download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${contract.fileName}_assinado.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpa a URL
      window.URL.revokeObjectURL(url);

      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar contrato:', error);
      toast.error('Erro ao preparar PDF. Tente novamente.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleNextPage = () => {
    setPageNumber(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1));
  };

  const handleMerchantIdChange = (value: string) => {
    setMerchantId(value);
    setPageNumber(1); // Reseta para primeira página ao filtrar
  };

  const handleApplyFilter = () => {
    setPageNumber(1); // Reseta para primeira página
    fetchSignedContracts().then(response => setSignedContractsRegister(response || []));
  };

  useEffect(() => {
    fetchSignedContracts().then(response => setSignedContractsRegister(response || []));
  }, [pageNumber]); // Recarrega quando muda a página

  useEffect(() => {
    if (signedContractsRegister.length > 0) {
      fetchContracts(signedContractsRegister.map(c => c.contractId))
        .then(response => {
          const filteredContracts: Contract[] = [];

          response.forEach(contract => {
            const filteredContractsRegister = signedContractsRegister.filter(cr => cr.contractId === contract.id);

            if (filteredContractsRegister.length === 0) {
              console.error(`Nenhum registro encontrado para contractId: ${contract.id}`);
              return null;
            }

            filteredContractsRegister.forEach(fcr => {
               const filteredContract = {
                ...contract,
                signatureDate: fcr.signatureDate,
                businessName: fcr.businessName,
                document: fcr.document,
                merchant: fcr.merchant
              }

              filteredContracts.push(filteredContract);
            })
          });

          filteredContracts.filter(Boolean); // Remove nulls
          setSignedContracts(filteredContracts);
        });
    } else {
      setSignedContracts([]);
    }
  }, [signedContractsRegister]);


  return (
    <>
      <LoadingOverlay show={isLoading} />

      <ContainerLight>
        <HeaderLight>
          <h1>Histórico de Contratos</h1>

          {user.userType === 3 && (
            <FilterContainer>
              <FilterInput
                type="text"
                placeholder="Filtrar por Merchant ID"
                value={merchantId}
                onChange={(e: any) => handleMerchantIdChange(e.target.value)}
              />
              <FilterButton onClick={handleApplyFilter}>
                Aplicar Filtro
              </FilterButton>
            </FilterContainer>
          )}
        </HeaderLight>

        {signedContracts.length === 0 ? (
          <EmptyState>
            <span>📄</span>
            <p>Nenhum contrato assinado encontrado.</p>
          </EmptyState>
        ) : (
          <HistoryContainer>
            {signedContracts.map(contract => (
              <HistoryCard key={contract.merchant + contract.id}>
                <HistoryCardTitle>{contract.fileName}</HistoryCardTitle>

                <ContractInfo>
                  <p><strong>Código do Estabelecimento:</strong> {contract.merchant}</p>
                  <p><strong>{formatDocument(contract.document).length === 18 ? 'Razão Social' : 'Nome'}:</strong> {formatBusinessName(contract.businessName)}</p>
                  <p><strong>Documento:</strong> {formatDocument(contract.document)}</p>
                  <p>
                    <strong>Assinado em:</strong>{' '}
                    {new Date(contract.signatureDate as string).toLocaleString('pt-BR')}
                  </p>
                </ContractInfo>

                {can(PortalPermissions.contratos.contratosTermos.baixarContrato) && (
                  <DownloadButton
                    onClick={() => handleDownloadContract(contract)}
                    disabled={downloadingId === contract.id}
                    $isDownloading={downloadingId === contract.id}
                  >
                    {downloadingId === contract.id
                      ? '⏳ Processando...'
                      : 'Baixar Contrato Assinado'}
                  </DownloadButton>
                )}
              </HistoryCard>
            ))}
          </HistoryContainer>
        )}
        {/* Controles de Paginação */}
        <PaginationContainer>
          <PaginationButton
            onClick={handlePreviousPage}
            disabled={pageNumber === 1}
          >
            ← Anterior
          </PaginationButton>

          <PageInfo>
            Página {pageNumber}
          </PageInfo>

          <PaginationButton
            onClick={handleNextPage}
            disabled={!hasMore}
          >
            Próxima →
          </PaginationButton>
        </PaginationContainer>
      </ContainerLight>
    </>
  );
}

export default ContractsHistory;
